import React, {Suspense, useEffect, useRef, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {HistoryDateCalendarProps} from '@di-shared/features/history/date/HistoryDateCalendar/index';
import useTripDayEu, {getWeekIndexOfDate, isTripOnDay} from '@di-shared/features/history/date/useTripDay.eu';
import useDrivingInfoEu from '@di-shared/features/history/driving-info/useDrivingInfo.eu';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {isSameYearAndMonth, startDateOfMonth} from '@shared/dateUtils';

export default function ({vin, selectedDate, setSelectedDate, isMonthMode, isMonthChange, setIsMonthChange}: HistoryDateCalendarProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          isMonthMode={isMonthMode}
          isMonthChange={isMonthChange}
          setIsMonthChange={setIsMonthChange}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, setSelectedDate, isMonthMode, isMonthChange, setIsMonthChange}: HistoryDateCalendarProps) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {verifiedDrivingDistances} = useDrivingInfoEu({vin});

  // viewDate: 달력이 표시 중인 월을 추적. selectedDate와 분리하여
  // onMonthChange에서 setSelectedDate(외부 네비게이션)를 중간에 호출하지 않도록 함.
  const [viewDate, setViewDate] = useState(selectedDate);

  const {focusTripDate, minDate, tripDays} = useTripDayEu({
    vin,
    selectedDate: viewDate,
    minPeriodDate: verifiedDrivingDistances[0].verifiedDate,
  });
  const [weekIndex, setWeekIndex] = useState(getWeekIndexOfDate(selectedDate));
  const focusTripDateRef = useRef(focusTripDate);
  focusTripDateRef.current = focusTripDate;

  // URL 네비게이션(뒤로가기 등) 외부 selectedDate 변경 시 viewDate 동기화
  /**
   * [2026-07-24:Jeyoung Lee]
   * 현재 날짜 7/24, 달력 6/30 에 포커스 되어있는 상황에서
   * 다음달로 (7월) 달력 변경하면 7/30 먼저 조회 후 달력이 오늘날짜로 동기화 되며 7/24 호출
   * 미래 날짜에 대한 조회를 하는 문제 해결
   */
  useEffect(() => {
    if (!isMonthChange) {
      setViewDate(selectedDate);
    }
  }, [selectedDate]);

  /**
   * [2026-03-12:Jeyoung Lee]
   * 월 변경 시에만 focusTripDate(해당 월의 주행 기록이 있는 날짜)로 이동
   * 조건 없이 항상 실행하면 URL의 날짜를 무시하고 focusTripDate로 변경되어
   * 새로고침이나 뒤로가기 시 URL과 선택된 날짜가 불일치하는 문제 발생
   *
   * [2026-03-18]
   * focusTripDate는 매 렌더마다 새 Date 객체로 생성되고, setSelectedDate(navigateToDate)는
   * innerDate 변경 시 새 참조가 생성되어 effect가 매 렌더마다 실행됨
   * navigate() 호출이 React 배칭을 방해해 isMonthChange=true 상태에서 effect가 재실행되는 루프 발생
   * isMonthChange만 dep으로 두고 focusTripDate는 ref로 읽어 루프 방지
   */
  useEffect(() => {
    if (isMonthChange) {
      setSelectedDate(focusTripDateRef.current);
      setWeekIndex(getWeekIndexOfDate(focusTripDateRef.current));
      setIsMonthChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonthChange]);

  // 월 변경 로딩화면
  if (isMonthChange) {
    return <FallbackLoading />;
  }

  return (
    <ReactDatePicker
      inline
      locale={getDateFnsLocale()}
      selected={selectedDate}
      minDate={minDate}
      maxDate={today}
      filterDate={(date) => isSameYearAndMonth(date, selectedDate)}
      disabledKeyboardNavigation
      calendarClassName={clsx(!isMonthMode && `show-first-week-${weekIndex + 1}`)}
      useWeekdaysShort
      onChange={(date: Date) => {
        setWeekIndex(getWeekIndexOfDate(date));
        setSelectedDate(date);
      }}
      onMonthChange={(date) => {
        setIsMonthChange(true);
        setViewDate(date);
      }}
      renderCustomHeader={({date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled}) => (
        <div className="react-datepicker__current-month">
          <button
            type="button"
            aria-label="Previous Month"
            className={clsx('react-datepicker__navigation react-datepicker__navigation--previous', {
              'navigation--disabled': prevMonthButtonDisabled,
            })}
            disabled={prevMonthButtonDisabled}
            onClick={() => decreaseMonth()}
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
          </button>
          {CldrDatetimeFormatter.format(date, 'yMMM')}
          <button
            type="button"
            aria-label="Next Month"
            className={clsx('react-datepicker__navigation react-datepicker__navigation--next', {
              'navigation--disabled': nextMonthButtonDisabled,
            })}
            disabled={nextMonthButtonDisabled}
            onClick={() => increaseMonth()}
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
          </button>
        </div>
      )}
      renderDayContents={(dayOfMonth, date: Date) => (
        <>
          {dayOfMonth}
          {isSameYearAndMonth(date, selectedDate) && isTripOnDay(tripDays, dayOfMonth) && (
            <span className="has_data">
              <span className="blind">{t('ctn_home_information')}</span>
            </span>
          )}
        </>
      )}
    />
  );
}

function FallbackLoading() {
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <div className="react-datepicker">
      <div className="react-datepicker__month-container skeleton">
        <div className="react-datepicker__header">
          <div className="react-datepicker__current-month skeleton">
            <button
              type="button"
              className="react-datepicker__navigation react-datepicker__navigation--previous"
              aria-label="Previous Month"
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">Previous Month</span>
            </button>
            {CldrDatetimeFormatter.format(today, 'yMMM')}
            <button
              type="button"
              className="react-datepicker__navigation react-datepicker__navigation--next navigation--disabled"
              aria-label="Next Month"
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">Next Month</span>
            </button>
          </div>
          <div
            className="react-datepicker__day-names"
            style={{height: '260px'}}
          />
        </div>
      </div>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  const startDate = isSameYearAndMonth(selectedDate, today) ? today : startDateOfMonth(selectedDate);

  return (
    <ReactDatePicker
      inline
      locale={getDateFnsLocale()}
      disabledKeyboardNavigation
      selected={startDate}
      onChange={() => {}}
      useWeekdaysShort
      renderCustomHeader={({date}) => (
        <div className="react-datepicker__current-month">
          <button
            type="button"
            aria-label="Previous Month"
            className="react-datepicker__navigation react-datepicker__navigation--previous"
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
          </button>
          {CldrDatetimeFormatter.format(date, 'yMMM')}
          <button
            type="button"
            aria-label="Next Month"
            className="react-datepicker__navigation react-datepicker__navigation--next"
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
          </button>
        </div>
      )}
    />
  );
}
